/**
 * `Dialog` (W1-12 ui-kit-i18n; contract DialogFrameProps, DESIGN.md §4.21): the modal frame every dialog uses.
 * Radix dialog underneath: focus is trapped inside and returns to the opener on close, Escape / the close
 * button / a click on the scrim call `onClose`, the title names the dialog and the description describes it.
 * Sizes: sm 400 px, md 560 px, lg 820 px, full (viewport minus a margin); the body scrolls, header and footer
 * stay visible.
 *
 * Focus starts on the first enabled control of the body, else the footer's primary button, else the dialog
 * itself, never on the close button (internal/layers.ts), so the first Escape closes the dialog. An Escape that
 * reverts a field's typed text (data-pending-edit) stays with the field; the next one closes the dialog.
 */

import * as RD from '@radix-ui/react-dialog';
import { useRef } from 'react';
import s from './Dialog.module.css';
import { IconButton } from './IconButton';
import { cx } from './internal/cx';
import { focusInitial, keepOpenForPendingEdit, useOpener } from './internal/layers';
import type { DialogFrameProps } from './types';
import { m } from './ui.messages';

export function Dialog(props: DialogFrameProps) {
  const size = props.size ?? 'md';
  const hasDescription = props.description !== undefined && props.description !== null;
  // Dialogs are opened by services (no Radix Trigger): remember the opener and give focus back to it on close.
  const opener = useOpener(props.open);
  const contentRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  return (
    <RD.Root
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <RD.Portal>
        <RD.Overlay className={s.overlay} />
        <RD.Content
          ref={contentRef}
          className={cx(s.dialog, s[size])}
          data-size={size}
          {...(hasDescription ? {} : { 'aria-describedby': undefined })}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            focusInitial({ content: contentRef.current, body: bodyRef.current, footer: footerRef.current });
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            const target = opener;
            if (target?.isConnected === true) target.focus({ preventScroll: true });
          }}
          onEscapeKeyDown={keepOpenForPendingEdit}
        >
          <header className={s.header}>
            <RD.Title className={s.title}>{props.title}</RD.Title>
            <IconButton
              icon="X"
              label={{ key: m.close }}
              className={s.close}
              onClick={() => {
                props.onClose();
              }}
            />
          </header>
          {hasDescription && <RD.Description className={s.description}>{props.description}</RD.Description>}
          <div ref={bodyRef} className={s.body}>
            {props.children}
          </div>
          {props.footer !== undefined && props.footer !== null && (
            <footer ref={footerRef} className={s.footer}>
              {props.footer}
            </footer>
          )}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
